import { useRef } from 'react'
import { Link } from 'react-router-dom'

import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IInAppWithAppIconUrl } from '~/types/iap'

import columns from './columns'
import pricesColumns from './pricesColumns'

import AppLogo from '~/components/AppLogo'
import Badge from '~/components/Badge'
import IapCard from '~/components/IapCard'
import InAppResume from '~/components/InAppResume'
import MwMLoader from '~/components/MwMLoader'
import Section from '~/components/Section'
import TableWrapper from '~/components/Tables/TableWrapper'
import Tooltip from '~/components/Tooltip'
import { FASTLY_PATH } from '~/const/asset'
import { CATEGORY_URLS } from '~/const/category-urls'
import { MAX_BADGES_LIMIT } from '~/const/iap'
import { useFeaturesGroupListQuery, useFeaturesListQuery, usePricesQuery } from '~/queries/iap'
import { resize } from '~/utils/media'

import './styles.scss'

const PRICES_ENTRIES_NAME = 'in-apps-panel-prices-list'

const FEATURES_ENTRIES_NAME = 'in-apps-panel-features-list'

interface Props {
  inAppDetails?: IInAppWithAppIconUrl
}

const PanelContent = ({ inAppDetails }: Props): ReactElement => {
  /** Local state */

  const featuresGridRef = useRef<AgGridReact>(null)

  const featuresTableRef = useRef<HTMLDivElement>(null)

  const pricesGridRef = useRef<AgGridReact>(null)

  const pricesTableRef = useRef<HTMLDivElement>(null)

  /** Queries & Mutations */

  const { data: prices, isLoading: isPricesLoading } = usePricesQuery({
    appKey: inAppDetails?.app_key,
    deviceType: inAppDetails?.device_type,
    sku: inAppDetails?.sku,
  })

  const { data: featuresGroup, isLoading: isFeaturesGroupLoading } = useFeaturesGroupListQuery({
    appKey: inAppDetails?.app_key,
    deviceType: inAppDetails?.device_type,
  })

  const { data: features, isLoading: isFeaturesLoading } = useFeaturesListQuery({
    appKey: inAppDetails?.app_key,
    deviceType: inAppDetails?.device_type,
  })

  const isLoading = isFeaturesGroupLoading || isPricesLoading || isFeaturesLoading

  /** Render */

  return (
    <div className="in-app-details-panel-content">
      <div className="in-app-details-panel-content__header">
        <AppLogo
          appLogoUrl={inAppDetails?.app_icon_url ? resize(`${FASTLY_PATH}${inAppDetails.app_icon_url}`, 150, 150) : ''}
          size="large"
        />
        {inAppDetails ? <InAppResume inApp={inAppDetails} /> : null}
      </div>
      {isLoading ? (
        <MwMLoader category="item" />
      ) : (
        <>
          <div className="in-app-details-panel-content__section prices-section">
            <p className="in-app-details-panel-content__section__title">Prices</p>
            <Section>
              <TableWrapper
                columnDefs={pricesColumns}
                entriesName={PRICES_ENTRIES_NAME}
                ref={pricesGridRef}
                rowData={prices}
                tableRef={pricesTableRef}
                withSearchBar
              />
            </Section>
          </div>
          <div className="in-app-details-panel-content__section">
            <p className="in-app-details-panel-content__section__title">Offers</p>
            {inAppDetails?.offers ? (
              <div className="in-app-details-panel-content__section__offers-container">
                {inAppDetails.offers?.map(({ name, offer_id }) => (
                  <Link key={offer_id} to={`${CATEGORY_URLS.IN_APP_PURCHASES}/offers/editor/${offer_id}`}>
                    <Badge
                      className="in-app-details-panel-content__section__offers-container__offer-badge"
                      outlined
                      size="medium"
                      value={name}
                    />
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
          <div className="in-app-details-panel-content__section">
            <p className="in-app-details-panel-content__section__title">Features Group</p>
            <div className="in-app-details-panel-content__section__features-group-container">
              {featuresGroup?.data?.map(({ feature_group_id, feature_ids, name }) => (
                <IapCard
                  content={
                    feature_ids ? (
                      <div className="iap-card-badge-container">
                        {feature_ids.slice(0, MAX_BADGES_LIMIT).map((id) => (
                          <Badge className="in-app-badge" key={id} outlined value={id} />
                        ))}
                        {feature_ids.length > MAX_BADGES_LIMIT ? (
                          <Tooltip
                            align={{ points: ['br', 'tr'] }}
                            overlay={
                              <div className="iap-card-badge-tooltip-content">
                                {feature_ids
                                  ?.slice(MAX_BADGES_LIMIT)
                                  .map((id) => <Badge className="in-app-badge" key={id} outlined value={id} />)}
                              </div>
                            }
                            placement="top"
                            trigger="hover"
                          >
                            <div className="iap-card-badge-container__items-counter">
                              +{feature_ids?.length - MAX_BADGES_LIMIT}
                            </div>
                          </Tooltip>
                        ) : null}
                      </div>
                    ) : (
                      <div className="no-feature-wrapper">
                        <span className="no-feature-wrapper__message">No Features in this group</span>
                      </div>
                    )
                  }
                  key={feature_group_id}
                  titleComponent={name}
                />
              ))}
            </div>
          </div>
          <div className="in-app-details-panel-content__section">
            <p className="in-app-details-panel-content__section__title">Features</p>
            <Section>
              <TableWrapper
                columnDefs={columns}
                entriesName={FEATURES_ENTRIES_NAME}
                ref={featuresGridRef}
                rowData={features?.data}
                tableRef={featuresTableRef}
              />
            </Section>
          </div>
        </>
      )}
    </div>
  )
}

export default PanelContent
